import * as React from 'react';

import { Button, Col, ConsoleDialog, Loading, Row } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideArrowLeft, LucideCopy } from 'lucide-react';

import {
  $cloneDialogSelectedAccountIds,
  $cloneQuote,
  $cloneQuoteId,
  $remoteCloneQuoteDetail,
  $resetCloneDialog,
} from '../../_state/quoteClone.state';
import { $remoteQuoteDetail } from '../../_state/quoteDetail.state';
import { $remoteQuotes } from '../../_state/quotesList.state';
import { useTranslations } from '../../i18n/useTranslations';

import { QuoteCloneWizardStepCustomers } from './QuoteCloneWizardStepCustomers';
import { QuoteCloneWizardStepSummary } from './QuoteCloneWizardStepSummary';

import { StepIndicator } from '~/modules/trade/_components/_shared/StepIndicator';

interface Step {
  number: number;
  title: string;
}

//
// ⚛️ ⚛️ ⚛️ QUOTE CLONE DIALOG ⚛️ ⚛️ ⚛️
//
export const QuoteCloneDialog: React.FC = () => {
  const t = useTranslations();

  // States
  const [currentStep, setCurrentStep] = React.useState(1);
  const cloneQuoteId = useAtomValue($cloneQuoteId);

  // Atoms
  const selectedAccountIds = useAtomValue($cloneDialogSelectedAccountIds);
  const { data: quote, isLoading } = useAtomValue($remoteCloneQuoteDetail);
  const { refetch: refetchQuoteDetail } = useAtomValue($remoteQuoteDetail);
  const { refetch: refetchQuotesList } = useAtomValue($remoteQuotes);
  const cloneQuote = useSetAtom($cloneQuote);
  const resetCloneDialog = useSetAtom($resetCloneDialog);

  const toast = useToast();

  const isOpen = cloneQuoteId !== null;

  // Memoized values
  const wizardSteps: Step[] = React.useMemo(
    () => [
      { number: 1, title: t.quotes.clone.steps.customers },
      { number: 2, title: t.quotes.clone.steps.summary },
    ],
    [t],
  );

  // Callbacks
  const handleClose = React.useCallback(() => {
    resetCloneDialog();
    setCurrentStep(1);
  }, [resetCloneDialog]);

  const handleBack = React.useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleNext = React.useCallback(() => {
    if (currentStep < wizardSteps.length) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, wizardSteps.length]);

  const handleClone = React.useCallback(async () => {
    if (!cloneQuoteId || selectedAccountIds.length === 0) return;
    try {
      await cloneQuote({ id: cloneQuoteId, accountIds: selectedAccountIds });
      toast.success(t.quotes.toast.cloned(selectedAccountIds.length));
      refetchQuoteDetail?.();
      refetchQuotesList?.();
      handleClose();
    } catch (err) {
      toast.error(
        t.quotes.toast.cloneError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    }
  }, [
    cloneQuoteId,
    selectedAccountIds,
    cloneQuote,
    toast,
    t,
    refetchQuoteDetail,
    refetchQuotesList,
    handleClose,
  ]);

  // Effects
  React.useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
    }
  }, [isOpen]);

  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === wizardSteps.length;
  const canProceed = selectedAccountIds.length > 0;

  const quoteType = quote?.type === 'Purchase' ? 'Purchase' : 'Sales';
  const dialogTitle = quote
    ? t.quotes.clone.title(quote.quoteNumber, quoteType)
    : '';

  return (
    <ConsoleDialog
      className="@QuoteCloneDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      size="fill"
      title={
        <Col gap3>
          <Row gap3 className="text-xl font-semibold">
            <span>{dialogTitle}</span>
          </Row>
          <StepIndicator steps={wizardSteps} currentStep={currentStep} />
        </Col>
      }
      titleClassName="bg-foreground/5 pt-3"
      content={
        isLoading || !quote ? (
          <Loading />
        ) : currentStep === 1 ? (
          <QuoteCloneWizardStepCustomers />
        ) : (
          <QuoteCloneWizardStepSummary quote={quote} />
        )
      }
      footer={
        <div className="flex w-full items-center justify-between gap-4">
          <div>
            {!isFirstStep && (
              <Button
                color="neutral"
                variant="outline"
                icon={<LucideArrowLeft className="size-4" />}
                onPress={handleBack}
              >
                {t.quotes.clone.buttons.back}
              </Button>
            )}
          </div>

          <div className="flex gap-3">
            {isLastStep ? (
              <Button
                icon={<LucideCopy className="size-4" />}
                color="primary"
                onPress={handleClone}
                isDisabled={!canProceed}
              >
                {t.quotes.clone.buttons.clone}
              </Button>
            ) : (
              <Button
                color="primary"
                variant="solid"
                onPress={handleNext}
                isDisabled={!canProceed}
              >
                {t.quotes.clone.buttons.next}
              </Button>
            )}
          </div>
        </div>
      }
    />
  );
};
